import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  AlertTriangle, Check, Laptop, RefreshCw, Search, ShieldCheck, Smartphone, Trash2, UserCheck,
} from 'lucide-react';
import {
  adminService, AdminDeviceRow, DeviceListStatus, DeviceReadiness, DeviceTrustPolicy,
} from '../../services/adminService';
import { ApiError } from '../../services/apiClient';
import { useToast } from '../../contexts/ToastContext';
import { ConfirmDialog } from '../ConfirmDialog';
import { Modal } from '../Modal';
import { TableSkeleton } from '../Skeleton';
import { ADMIN_LIST_FILL, ADMIN_TAB_FILL } from './adminLayout';

/**
 * 관리자 콘솔 '기기 승인' 탭 (super_admin 전용 — server/routes/admin.js의 /devices·/device-policy).
 *
 * 웹 브라우저는 막고, PC 앱과 승인된 모바일 기기만 쓰게 한다(server/services/deviceTrust.js).
 * - 정책: 꺼짐 → 관찰(차단 없이 등록·요청만) → 강제 적용. 강제 적용 전에 점검표로 못 들어오게 될 사람을 본다
 * - 승인 대기: 직원이 자기 PC 앱에서 본인 확인을 했으면 바로 승인, 아니면 직원에게 받은 확인 코드를 넣는다
 *   ⚠ 확인 코드는 이 화면에 절대 나오지 않는다 — 관리자가 직원과 실제로 확인했다는 증거이기 때문이다
 */

const STATUS_TABS: { key: DeviceListStatus; label: string }[] = [
  { key: 'pending', label: '승인 대기' },
  { key: 'approved', label: '등록된 기기' },
  { key: 'closed', label: '처리 기록' },
];

const STATUS_LABEL: Record<AdminDeviceRow['status'], string> = {
  pending: '대기',
  approved: '승인',
  rejected: '거절',
  revoked: '해제',
  expired: '만료',
};

function platformSummary(row: AdminDeviceRow): string {
  if (row.kind === 'desktop') return row.computerName ? `PC 이름 ${row.computerName}` : 'PC 이름 없음';
  const ua = row.platform?.ua || '';
  const os = /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
    : /Android/.test(ua) ? 'Android'
    : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'Mac'
    : '알 수 없는 기기';
  const browser = /SamsungBrowser/.test(ua) ? '삼성 인터넷'
    : /Edg\//.test(ua) ? 'Edge'
    : /CriOS|Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : '';
  const app = row.platform?.standalone ? '홈 화면 앱' : '브라우저';
  return [os, browser, app].filter(Boolean).join(' · ');
}

function formatTime(iso: string | null): string {
  if (!iso) return '-';
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return '방금 전';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}분 전`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}시간 전`;
  return new Date(iso).toLocaleString('ko-KR');
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

type ReasonDialog = { kind: 'reject' | 'revoke'; row: AdminDeviceRow } | null;

export const DeviceTrustManager: React.FC = () => {
  const fid = useId();
  const { showToast } = useToast();
  const [policy, setPolicy] = useState<DeviceTrustPolicy | null>(null);
  const [readiness, setReadiness] = useState<DeviceReadiness | null>(null);
  const [status, setStatus] = useState<DeviceListStatus>('pending');
  const [kind, setKind] = useState<'' | 'desktop' | 'web'>('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<AdminDeviceRow[]>([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [policyBusy, setPolicyBusy] = useState(false);
  const [confirmMode, setConfirmMode] = useState<DeviceTrustPolicy['mode'] | null>(null);

  // 승인 창
  const [approveRow, setApproveRow] = useState<AdminDeviceRow | null>(null);
  const [code, setCode] = useState('');
  const [limitDevices, setLimitDevices] = useState<AdminDeviceRow[] | null>(null);
  const [replaceId, setReplaceId] = useState<number | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // 거절·해제 사유 창
  const [reasonDialog, setReasonDialog] = useState<ReasonDialog>(null);
  const [reason, setReason] = useState('');
  const [deleteRow, setDeleteRow] = useState<AdminDeviceRow | null>(null);

  const loadList = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await adminService.getDevices({ status, kind, q: query.trim() });
      setRows(res.rows);
      setCounts(res.counts);
    } catch (err) {
      setLoadError(errorMessage(err, '기기 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setLoading(false);
    }
  }, [status, kind, query]);

  const loadPolicy = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([adminService.getDevicePolicy(), adminService.getDeviceReadiness()]);
      setPolicy(p.policy);
      setReadiness(r);
    } catch (err) {
      setLoadError(errorMessage(err, '정책을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'));
    }
  }, []);

  useEffect(() => { loadPolicy(); }, [loadPolicy]);
  // 검색어는 타이핑이 멈춘 뒤에 조회한다
  useEffect(() => {
    const t = setTimeout(loadList, query ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadList, query]);
  // 대기 목록 변화(새 요청·본인 확인·신고)는 소켓으로 온다
  useEffect(() => {
    const onChanged = () => { loadList(); adminService.getDeviceReadiness().then(setReadiness).catch(() => {}); };
    const onReported = (e: Event) => {
      const name = (e as CustomEvent<{ userName?: string }>).detail?.userName;
      showToast({ type: 'error', message: `${name || '직원'}님이 자기 요청이 아닌 기기 승인 요청을 신고했습니다. 비밀번호 도용을 확인해 주세요.`, duration: 12000 });
    };
    window.addEventListener('device:pending-changed', onChanged);
    window.addEventListener('device:reported', onReported);
    return () => {
      window.removeEventListener('device:pending-changed', onChanged);
      window.removeEventListener('device:reported', onReported);
    };
  }, [loadList, showToast]);

  const applyPolicy = async (patch: Partial<DeviceTrustPolicy>) => {
    setPolicyBusy(true);
    try {
      const res = await adminService.updateDevicePolicy(patch);
      setPolicy(res.policy);
      showToast({
        type: 'success',
        message: patch.mode === 'enforce'
          ? `강제 적용했습니다. 승인 기기에 묶이지 않은 알림 구독 ${res.purgedSubscriptions}건을 정리했습니다.`
          : '저장했습니다.',
      });
      adminService.getDeviceReadiness().then(setReadiness).catch(() => {});
    } catch (err) {
      showToast({ type: 'error', message: errorMessage(err, '정책을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.') });
    } finally {
      setPolicyBusy(false);
    }
  };

  const openApprove = (row: AdminDeviceRow) => {
    setApproveRow(row);
    setCode('');
    setLimitDevices(null);
    setReplaceId(null);
    setActionError(null);
  };

  const submitApprove = async () => {
    if (!approveRow) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await adminService.approveDevice(approveRow.id, {
        code: approveRow.selfConfirmed ? undefined : code,
        replaceDeviceId: replaceId ?? undefined,
      });
      showToast({ type: 'success', message: `${approveRow.userName || approveRow.userId}님의 기기를 승인했습니다.` });
      setApproveRow(null);
      loadList();
      adminService.getDeviceReadiness().then(setReadiness).catch(() => {});
    } catch (err) {
      if (err instanceof ApiError && err.code === 'DEVICE_LIMIT') {
        setLimitDevices((err.body?.devices as AdminDeviceRow[]) || []);
        setActionError(err.message);
      } else {
        setActionError(errorMessage(err, '승인하지 못했습니다. 잠시 후 다시 시도해 주세요.'));
        if (err instanceof ApiError && err.code === 'CODE_LOCKED') { setApproveRow(null); loadList(); }
      }
    } finally {
      setActionBusy(false);
    }
  };

  const submitReason = async () => {
    if (!reasonDialog) return;
    const text = reason.trim();
    if (reasonDialog.kind === 'revoke' && !text) return;
    setActionBusy(true);
    try {
      if (reasonDialog.kind === 'reject') await adminService.rejectDevice(reasonDialog.row.id, text);
      else await adminService.revokeDevice(reasonDialog.row.id, text);
      showToast({ type: 'success', message: reasonDialog.kind === 'reject' ? '요청을 거절했습니다.' : '기기 승인을 해제했습니다.' });
      setReasonDialog(null);
      setReason('');
      loadList();
    } catch (err) {
      showToast({ type: 'error', message: errorMessage(err, '처리하지 못했습니다. 잠시 후 다시 시도해 주세요.') });
    } finally {
      setActionBusy(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteRow) return;
    const row = deleteRow;
    setDeleteRow(null);
    try {
      await adminService.deleteDeviceRecord(row.id);
      showToast({ type: 'success', message: '기록을 삭제했습니다. 같은 기기로 다시 요청할 수 있습니다.' });
      loadList();
    } catch (err) {
      showToast({ type: 'error', message: errorMessage(err, '기록을 삭제하지 못했습니다. 잠시 후 다시 시도해 주세요.') });
    }
  };

  const mode = policy?.mode || 'off';
  const modeTabs = useMemo(() => ([
    { key: 'off' as const, label: '꺼짐', hint: '지금처럼 웹 브라우저로도 로그인할 수 있습니다.' },
    { key: 'observe' as const, label: '관찰', hint: '차단하지 않고 기기 등록·승인 요청만 받습니다. 강제 적용 전 준비 단계입니다.' },
    { key: 'enforce' as const, label: '강제 적용', hint: 'PC 앱과 승인된 모바일 기기만 로그인할 수 있습니다. 웹 브라우저는 막힙니다.' },
  ]), []);

  return (
    <div className={ADMIN_TAB_FILL}>
      {/* 정책 */}
      <section className="mb-4 p-4 bg-white border border-slate-200 rounded-xl space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className={mode === 'enforce' ? 'text-brand-600' : 'text-slate-400'} aria-hidden="true" />
              <span className="text-sm font-medium text-slate-800">기기 신뢰 정책</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">{modeTabs.find((m) => m.key === mode)?.hint}</p>
            {policy?.updatedAt && (
              <p className="mt-0.5 text-xs text-slate-400">
                마지막 변경: {policy.updatedByName} · {new Date(policy.updatedAt).toLocaleString('ko-KR')}
              </p>
            )}
          </div>
          <div className="shrink-0 inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" role="radiogroup" aria-label="정책 모드">
            {modeTabs.map((m) => (
              <button
                key={m.key}
                type="button"
                role="radio"
                aria-checked={mode === m.key}
                disabled={policyBusy || !!policy?.envKillSwitch || mode === m.key}
                onClick={() => setConfirmMode(m.key)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors disabled:cursor-default ${
                  mode === m.key ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {policy?.envKillSwitch && (
          <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>서버 설정(DEVICE_TRUST_ENABLED=false)으로 꺼져 있습니다. 이 화면에서는 바꿀 수 없습니다.</span>
          </div>
        )}

        {policy && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
            <label className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-slate-200">
              <span className="text-slate-700">사내망 첫 로그인 PC 자동 승인</span>
              <input
                type="checkbox"
                className="h-4 w-4 accent-brand-600"
                checked={policy.desktopAutoApproveInternal}
                disabled={policyBusy || !!policy.envKillSwitch}
                onChange={(e) => applyPolicy({ desktopAutoApproveInternal: e.target.checked })}
              />
            </label>
            <NumberSetting
              id={`${fid}-maxweb`} label="1인당 모바일 기기" suffix="대" value={policy.maxWebDevicesPerUser} min={1} max={5}
              disabled={policyBusy || !!policy.envKillSwitch} onSave={(v) => applyPolicy({ maxWebDevicesPerUser: v })}
            />
            <NumberSetting
              id={`${fid}-resume`} label="모바일 로그인 유지" suffix="일" value={policy.webResumeDays} min={1} max={90}
              disabled={policyBusy || !!policy.envKillSwitch} onSave={(v) => applyPolicy({ webResumeDays: v })}
            />
            <NumberSetting
              id={`${fid}-inactive`} label="미사용 기기 자동 만료" suffix="일" value={policy.webInactivityDays} min={7} max={365}
              disabled={policyBusy || !!policy.envKillSwitch} onSave={(v) => applyPolicy({ webInactivityDays: v })}
            />
          </div>
        )}

        {/* 강제 적용 전 점검표 */}
        {readiness && mode !== 'enforce' && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1.5">
            <div className="font-medium text-slate-800">강제 적용 전 점검 (최근 30일 접속자 {readiness.recentUsers}명 기준)</div>
            <ReadinessLine ok={readiness.pcWithoutDevice.length === 0} text={`승인된 PC가 없는 PC 앱 사용자 ${readiness.pcWithoutDevice.length}명`}
              names={readiness.pcWithoutDevice.map((u) => u.userName || u.userId)} />
            <ReadinessLine ok={readiness.webOnly.length === 0} text={`웹으로만 접속한 사용자 ${readiness.webOnly.length}명 (강제 적용 후 PC 앱이나 승인된 모바일이 필요)`}
              names={readiness.webOnly.map((u) => u.userName || u.userId)} />
            <ReadinessLine ok={readiness.legacyDesktop.length === 0} text={`키가 없는 구버전 PC 앱으로 로그인한 사용자(최근 7일) ${readiness.legacyDesktop.length}명`}
              names={readiness.legacyDesktop.map((u) => u.name || u.sabun)} />
            <ReadinessLine ok={readiness.adminsWithoutDesktop.length === 0} text={`승인된 PC가 없는 관리자 ${readiness.adminsWithoutDesktop.length}명`}
              names={readiness.adminsWithoutDesktop.map((u) => u.userId)} />
            <ReadinessLine ok={readiness.helpdeskWithoutDesktop.length === 0} text={`승인된 PC가 없는 원격지원 담당자 ${readiness.helpdeskWithoutDesktop.length}명`}
              names={readiness.helpdeskWithoutDesktop.map((u) => u.userName || u.userId)} />
            <ReadinessLine ok={readiness.pending === 0} text={`처리하지 않은 승인 요청 ${readiness.pending}건`} />
            <ReadinessLine ok={readiness.requesterReady} text={readiness.requesterReady ? '지금 쓰는 기기는 강제 적용 후에도 들어올 수 있습니다' : '지금 쓰는 기기가 승인되지 않았습니다 — 승인된 PC 앱에서 강제 적용해 주세요'} />
          </div>
        )}
      </section>

      {/* 목록 */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" role="tablist" aria-label="기기 목록">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={status === t.key}
              onClick={() => { setStatus(t.key); setLoading(true); }}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                status === t.key ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {t.label}
              <span className={`ml-1.5 text-xs ${t.key === 'pending' && counts.pending > 0 ? 'text-brand-700 font-semibold' : 'text-slate-400'}`}>
                {counts[t.key as keyof typeof counts]}
              </span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as '' | 'desktop' | 'web')}
            className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg bg-white"
            aria-label="기기 종류"
          >
            <option value="">전체 기기</option>
            <option value="desktop">PC 앱</option>
            <option value="web">모바일</option>
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="이름, 부서, PC 이름으로 찾기"
              className="pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg w-56"
            />
          </div>
          <button
            type="button"
            onClick={() => { setLoading(true); loadList(); loadPolicy(); }}
            className="p-2 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-100"
            aria-label="새로고침"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {loadError && (
        <div className="mb-3 px-3 py-2 text-sm text-brand-700 bg-brand-50 border border-brand-100 rounded-lg">{loadError}</div>
      )}

      <div className={`${ADMIN_LIST_FILL} border border-slate-200 rounded-xl bg-white divide-y divide-slate-100`}>
        {loading ? (
          <div className="p-4"><TableSkeleton rows={4} /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm text-slate-600">
              {status === 'pending' ? '처리할 승인 요청이 없습니다' : status === 'approved' ? '등록된 기기가 없습니다' : '처리 기록이 없습니다'}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {status === 'pending' ? '직원이 모바일·사외 PC에서 승인을 요청하면 여기에 표시됩니다' : '정책을 관찰 이상으로 켜면 기기가 등록됩니다'}
            </p>
          </div>
        ) : rows.map((row) => (
          <DeviceRow
            key={row.id}
            row={row}
            onApprove={() => openApprove(row)}
            onReject={() => { setReasonDialog({ kind: 'reject', row }); setReason(''); }}
            onRevoke={() => { setReasonDialog({ kind: 'revoke', row }); setReason(''); }}
            onDelete={() => setDeleteRow(row)}
          />
        ))}
      </div>

      {/* 승인 창 */}
      <Modal
        isOpen={!!approveRow}
        onClose={() => setApproveRow(null)}
        title="기기 승인"
        size="sm"
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setApproveRow(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
            <button
              type="button"
              onClick={submitApprove}
              disabled={actionBusy || (!approveRow?.selfConfirmed && code.replace(/\D/g, '').length !== 6) || (!!limitDevices && replaceId == null)}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50"
            >
              {actionBusy ? '승인 중…' : '승인'}
            </button>
          </div>
        )}
      >
        {approveRow && (
          <div className="space-y-4 text-sm">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1">
              <div className="font-medium text-slate-900">{approveRow.userName || approveRow.userId} <span className="text-slate-500 font-normal">{approveRow.depart}</span></div>
              <div className="text-slate-600">{approveRow.label || (approveRow.kind === 'desktop' ? 'PC 앱' : '모바일')} · {platformSummary(approveRow)}</div>
              <div className="text-slate-500 text-xs">기기 지문 {approveRow.fingerprint} · 요청 {formatTime(approveRow.requestedAt)} · {approveRow.officeNetwork ? '사내망' : '사외'} {approveRow.requestIp || ''}</div>
            </div>
            {approveRow.selfConfirmed ? (
              <p className="flex items-start gap-2 text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <UserCheck size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                직원이 이미 승인된 자기 기기에서 확인 코드를 입력해 본인 확인을 마쳤습니다.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-slate-600 leading-relaxed">
                  직원에게 <b>PC 앱 1:1 대화나 내선</b>으로 직접 연락해, 요청한 기기 화면에 뜬 6자리 확인 코드를 받아 입력하세요.
                  요청에 적힌 정보로 연락하지 마세요 — 비밀번호를 도용한 사람이 넣은 요청일 수 있습니다.
                </p>
                <label htmlFor={`${fid}-code`} className="block text-xs font-medium text-slate-700">확인 코드</label>
                <input
                  id={`${fid}-code`}
                  inputMode="numeric"
                  autoComplete="off"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, '').slice(0, 7))}
                  className="block w-full px-3 py-2.5 text-center text-xl tracking-widest tabular-nums border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-400"
                  placeholder="000 000"
                />
                {approveRow.verifyAttempts > 0 && (
                  <p className="text-xs text-slate-500">잘못 입력 {approveRow.verifyAttempts}회 — 5회가 되면 요청이 거절됩니다.</p>
                )}
              </div>
            )}
            {limitDevices && (
              <div className="space-y-2">
                <p className="text-slate-700">해제할 기존 기기를 고르세요.</p>
                {limitDevices.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 cursor-pointer">
                    <input type="radio" name={`${fid}-replace`} checked={replaceId === d.id} onChange={() => setReplaceId(d.id)} className="accent-brand-600" />
                    <span>{d.label || '모바일'} · {platformSummary(d)} · 최근 사용 {formatTime(d.lastSeenAt)}</span>
                  </label>
                ))}
              </div>
            )}
            {actionError && <p className="text-sm text-brand-700">{actionError}</p>}
          </div>
        )}
      </Modal>

      {/* 거절·해제 사유 */}
      <Modal
        isOpen={!!reasonDialog}
        onClose={() => setReasonDialog(null)}
        title={reasonDialog?.kind === 'reject' ? '요청 거절' : '기기 승인 해제'}
        size="sm"
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setReasonDialog(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
            <button
              type="button"
              onClick={submitReason}
              disabled={actionBusy || (reasonDialog?.kind === 'revoke' && !reason.trim())}
              className="px-4 py-2 text-sm font-medium bg-white border border-brand-600 text-brand-600 hover:bg-brand-50 rounded-lg disabled:opacity-50"
            >
              {reasonDialog?.kind === 'reject' ? '거절' : '해제'}
            </button>
          </div>
        )}
      >
        {reasonDialog && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              {reasonDialog.row.userName || reasonDialog.row.userId}님의 {reasonDialog.row.label || (reasonDialog.row.kind === 'desktop' ? 'PC' : '모바일 기기')}
              {reasonDialog.kind === 'reject'
                ? ' 요청을 거절할까요? 같은 기기는 24시간 동안 다시 요청할 수 없습니다(PC는 기록을 지우기 전까지).'
                : ' 승인을 해제할까요? 그 기기에서 쓰던 세션이 바로 끊기고 알림도 멈춥니다.'}
            </p>
            <label htmlFor={`${fid}-reason`} className="block text-xs font-medium text-slate-700">
              사유{reasonDialog.kind === 'revoke' ? ' (필수)' : ' (선택)'}
            </label>
            <input
              id={`${fid}-reason`}
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
              className="block w-full px-3 py-2 border border-slate-300 rounded-lg"
              placeholder={reasonDialog.kind === 'revoke' ? '예: 분실 신고, 퇴사' : '예: 본인 확인 불가'}
            />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={confirmMode !== null}
        title={confirmMode === 'enforce' ? '강제 적용' : '정책 변경'}
        message={confirmMode === 'enforce'
          ? `웹 브라우저와 승인되지 않은 기기의 로그인을 지금부터 막을까요? 접속 중인 미승인 세션이 끊기고, 승인 기기에 묶이지 않은 알림 구독이 정리됩니다.${readiness ? ` 최근 30일 접속자 중 ${readiness.lockedOut.length}명이 승인된 기기가 없습니다.` : ''}`
          : confirmMode === 'off'
            ? '기기 신뢰를 끌까요? 웹 브라우저로도 다시 로그인할 수 있게 됩니다.'
            : '관찰 모드로 바꿀까요? 차단 없이 기기 등록과 승인 요청만 받습니다.'}
        confirmLabel={confirmMode === 'enforce' ? '강제 적용' : '변경'}
        danger={confirmMode === 'enforce' || confirmMode === 'off'}
        onConfirm={() => { const m = confirmMode; setConfirmMode(null); if (m) applyPolicy({ mode: m }); }}
        onCancel={() => setConfirmMode(null)}
      />

      <ConfirmDialog
        isOpen={!!deleteRow}
        title="기록 삭제"
        message="이 기록을 삭제할까요? 삭제하면 같은 기기에서 다시 승인을 요청할 수 있습니다(PC는 사내망이면 다시 자동 승인될 수 있습니다)."
        confirmLabel="삭제"
        danger
        onConfirm={submitDelete}
        onCancel={() => setDeleteRow(null)}
      />
    </div>
  );
};

const ReadinessLine: React.FC<{ ok: boolean; text: string; names?: string[] }> = ({ ok, text, names }) => (
  <div className="flex items-start gap-1.5">
    {ok
      ? <Check size={13} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
      : <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />}
    <span>
      {text}
      {names && names.length > 0 && (
        <span className="text-slate-400"> — {names.slice(0, 8).join(', ')}{names.length > 8 ? ` 외 ${names.length - 8}명` : ''}</span>
      )}
    </span>
  </div>
);

const NumberSetting: React.FC<{
  id: string; label: string; suffix: string; value: number; min: number; max: number; disabled: boolean;
  onSave: (v: number) => void;
}> = ({ id, label, suffix, value, min, max, disabled, onSave }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const commit = () => {
    const n = Number(draft);
    if (!Number.isInteger(n) || n < min || n > max) { setDraft(String(value)); return; }
    if (n !== value) onSave(n);
  };
  return (
    <label htmlFor={id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-slate-200">
      <span className="text-slate-700">{label}</span>
      <span className="flex items-center gap-1">
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          className="w-16 px-2 py-1 text-right border border-slate-300 rounded-md"
        />
        <span className="text-slate-500">{suffix}</span>
      </span>
    </label>
  );
};

const DeviceRow: React.FC<{
  row: AdminDeviceRow;
  onApprove: () => void;
  onReject: () => void;
  onRevoke: () => void;
  onDelete: () => void;
}> = ({ row, onApprove, onReject, onRevoke, onDelete }) => {
  const Icon = row.kind === 'desktop' ? Laptop : Smartphone;
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3">
      <Icon size={18} className="shrink-0 text-slate-500" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-slate-900">{row.userName || row.userId}</span>
          <span className="text-xs text-slate-500">{row.depart}</span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{row.kind === 'desktop' ? 'PC 앱' : '모바일'}</span>
          {row.status !== 'pending' && row.status !== 'approved' && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{STATUS_LABEL[row.status]}</span>
          )}
          {row.autoReason && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
              {row.autoReason === 'migration' ? '기존 설치 이관' : '사내망 자동'}
            </span>
          )}
          {row.status === 'pending' && row.selfConfirmed && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">본인 확인됨</span>
          )}
          {row.isMine && <span className="text-xs px-1.5 py-0.5 rounded bg-brand-100 text-brand-700">지금 이 기기</span>}
        </div>
        <div className="mt-0.5 text-xs text-slate-500 truncate">
          {row.label ? `${row.label} · ` : ''}{platformSummary(row)} · 지문 {row.fingerprint}
        </div>
        <div className="text-xs text-slate-400">
          {row.status === 'pending'
            ? `요청 ${formatTime(row.requestedAt)} · ${row.officeNetwork ? '사내망' : '사외'} ${row.requestIp || ''}`
            : row.status === 'approved'
              ? `최근 사용 ${formatTime(row.lastSeenAt)}${row.lastSeenIp ? ` · ${row.lastSeenIp}` : ''} · 승인 ${row.decidedByName || ''} ${formatTime(row.decidedAt)}`
              : `${STATUS_LABEL[row.status]} ${formatTime(row.decidedAt)} · ${row.decidedByName || ''}${row.decisionReason ? ` · ${row.decisionReason}` : ''}`}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {row.status === 'pending' && (
          <>
            <button type="button" onClick={onApprove} className="px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg">승인</button>
            <button type="button" onClick={onReject} className="px-3 py-1.5 text-sm bg-white border border-brand-600 text-brand-600 hover:bg-brand-50 rounded-lg">거절</button>
          </>
        )}
        {row.status === 'approved' && (
          <button
            type="button"
            onClick={onRevoke}
            disabled={row.isMine}
            title={row.isMine ? '지금 사용 중인 기기는 해제할 수 없습니다' : undefined}
            className="px-3 py-1.5 text-sm bg-white border border-brand-600 text-brand-600 hover:bg-brand-50 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
          >
            해제
          </button>
        )}
        {row.status !== 'pending' && row.status !== 'approved' && (
          <button type="button" onClick={onDelete} className="p-2 text-slate-400 hover:text-brand-700 rounded-lg hover:bg-slate-100" aria-label="기록 삭제">
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  );
};
